// No React hooks needed directly - using useToast from feedback
import { ToastProvider, useToast } from '@components/base/feedback';
import { Button } from '@components/base/actions';
import { Text } from '@components/base/display';
import { VStack, HStack, Card, CardContent } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

type ToastVariant = 'info' | 'success' | 'warning' | 'error';

/**
 * Inner component that uses the toast hook
 * Must be inside ToastProvider
 */
function ToastShowcaseInner({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const { toast, dismissAll } = useToast();

  const showPlaygroundToast = () => {
    toast({
      variant: playgroundProps.variant as ToastVariant,
      title: 'Playground Toast',
      message: 'This toast is configured from the Props Playground.',
      duration: playgroundProps.duration as number,
    });
  };

  const showVariantToast = (variant: ToastVariant) => {
    const messages: Record<ToastVariant, { title: string; message: string }> = {
      info: {
        title: 'Session Information',
        message: 'Your session has been active for 30 minutes.',
      },
      success: {
        title: 'Claim Submitted',
        message: 'Claim CLM-2024-007890 has been submitted successfully.',
      },
      warning: {
        title: 'Pending Documents',
        message: 'Please upload supporting documents within 7 days.',
      },
      error: {
        title: 'Submission Failed',
        message: 'Unable to submit claim. Please check your connection.',
      },
    };

    toast({
      variant,
      ...messages[variant],
    });
  };

  const showMessageOnlyToast = () => {
    toast({
      variant: 'info',
      message: 'Auto-save enabled. Your changes are being saved automatically.',
    });
  };

  const showTitleOnlyToast = () => {
    toast({
      variant: 'success',
      title: 'Changes saved successfully!',
    });
  };

  const showPersistentToast = () => {
    toast({
      variant: 'warning',
      title: 'Action Required',
      message: 'This claim requires your immediate attention. Click to dismiss.',
      duration: 0, // Never auto-dismiss
    });
  };

  const showMultipleToasts = () => {
    toast({
      variant: 'info',
      title: 'First Notification',
      message: 'This is the first toast in the stack.',
    });
    setTimeout(() => {
      toast({
        variant: 'success',
        title: 'Second Notification',
        message: 'This is the second toast.',
      });
    }, 300);
    setTimeout(() => {
      toast({
        variant: 'warning',
        title: 'Third Notification',
        message: 'This is the third toast.',
      });
    }, 600);
  };

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="toast">
          <HStack gap={4}>
            <Button onClick={showPlaygroundToast}>
              Show Toast
            </Button>
            <Button variant="secondary" onClick={dismissAll}>
              Dismiss All
            </Button>
          </HStack>
      </ShowcasePreviewSection>

      {/* Variants */}
      <ShowcaseDemoSection
        title="Variants"
        description="Four semantic variants for different notification types"
       
      >
          <HStack gap={4} wrap="wrap">
            <Button variant="secondary" onClick={() => showVariantToast('info')}>
              Info Toast
            </Button>
            <Button variant="secondary" onClick={() => showVariantToast('success')}>
              Success Toast
            </Button>
            <Button variant="secondary" onClick={() => showVariantToast('warning')}>
              Warning Toast
            </Button>
            <Button variant="secondary" onClick={() => showVariantToast('error')}>
              Error Toast
            </Button>
          </HStack>
      </ShowcaseDemoSection>

      {/* With Title and Message */}
      <ShowcaseDemoSection
        title="Content Options"
        description="Toasts can have title only, message only, or both"
       
      >
          <HStack gap={4} wrap="wrap">
            <Button variant="secondary" onClick={showTitleOnlyToast}>
              Title Only
            </Button>
            <Button variant="secondary" onClick={showMessageOnlyToast}>
              Message Only
            </Button>
            <Button variant="secondary" onClick={() => showVariantToast('success')}>
              Title + Message
            </Button>
          </HStack>
      </ShowcaseDemoSection>

      {/* Auto-dismiss vs Persistent */}
      <ShowcaseDemoSection
        title="Auto-dismiss vs Persistent"
        description="Control how long toasts remain visible (default 5 seconds, 0 for persistent)"
       
      >
          <VStack gap={4}>
            <HStack gap={4} wrap="wrap">
              <Button
                variant="secondary"
                onClick={() => {
                  toast({
                    variant: 'info',
                    title: 'Quick Toast',
                    message: 'This disappears in 2 seconds.',
                    duration: 2000,
                  });
                }}
              >
                2 Second Toast
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  toast({
                    variant: 'info',
                    title: 'Normal Toast',
                    message: 'This disappears in 5 seconds (default).',
                    duration: 5000,
                  });
                }}
              >
                5 Second Toast
              </Button>
              <Button variant="secondary" onClick={showPersistentToast}>
                Persistent Toast
              </Button>
            </HStack>
            <Text size="sm" color="secondary">
              Persistent toasts (duration: 0) must be manually dismissed by the user.
            </Text>
          </VStack>
      </ShowcaseDemoSection>

      {/* Stacking Multiple Toasts */}
      <ShowcaseDemoSection
        title="Stacking Multiple Toasts"
        description="Multiple toasts stack vertically (max 3 visible by default)"
       
      >
          <VStack gap={4}>
            <HStack gap={4}>
              <Button variant="secondary" onClick={showMultipleToasts}>
                Show 3 Toasts
              </Button>
              <Button variant="secondary" onClick={dismissAll}>
                Dismiss All
              </Button>
            </HStack>
            <Text size="sm" color="secondary">
              When more than 3 toasts are shown, older toasts are automatically removed.
            </Text>
          </VStack>
      </ShowcaseDemoSection>

      {/* Real-world Examples */}
      <ShowcaseDemoSection
        title="Real-world Examples"
        description="Common toast patterns in insurance applications"
       
      >
          <Card>
            <CardContent>
              <VStack gap={4}>
                <Text weight="semibold">Insurance Workflow Actions</Text>
                <HStack gap={4} wrap="wrap">
                  <Button
                    size="sm"
                    onClick={() => {
                      toast({
                        variant: 'success',
                        title: 'Policy Created',
                        message: 'Policy POL-2024-001842 has been created and is now active.',
                      });
                    }}
                  >
                    Create Policy
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      toast({
                        variant: 'success',
                        title: 'Document Uploaded',
                        message: 'Medical records have been added to claim CLM-2024-005678.',
                      });
                    }}
                  >
                    Upload Document
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      toast({
                        variant: 'warning',
                        title: 'Review Required',
                        message: 'Claim CLM-2024-003456 requires additional assessment.',
                        duration: 8000,
                      });
                    }}
                  >
                    Flag for Review
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      toast({
                        variant: 'error',
                        title: 'Payment Failed',
                        message: 'Unable to process premium payment. Please verify payment details.',
                        duration: 0,
                      });
                    }}
                  >
                    Simulate Error
                  </Button>
                </HStack>
              </VStack>
            </CardContent>
          </Card>
      </ShowcaseDemoSection>
    </VStack>
  );
}

/**
 * Wrapper component that provides ToastProvider context
 */
function ToastShowcaseComponent(props: Readonly<ShowcaseProps>) {
  return (
    <ToastProvider>
      <ToastShowcaseInner {...props} />
    </ToastProvider>
  );
}

export const ToastShowcase: ShowcaseDefinition = {
  id: 'toast',
  name: 'Toast',
  category: 'feedback',
  description: 'Temporary notification messages',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['info', 'success', 'warning', 'error'],
      defaultValue: 'info',
      description: 'The visual style variant of the toast',
    },
    {
      name: 'duration',
      type: 'number',
      defaultValue: 5000,
      description: 'Auto-dismiss duration in milliseconds (0 to disable)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="status"',
      'aria-live="polite"',
    ],
    keyboardNav: [
      'Tab: Focus dismiss button',
      'Enter/Space: Dismiss toast',
    ],
    notes: [
      'Toasts are announced to screen readers via aria-live',
      'Uses polite announcement to avoid interrupting',
      'Dismiss button is keyboard accessible',
      'Animation respects prefers-reduced-motion preference',
    ],
  },
  component: ToastShowcaseComponent,
};
